import React, { useEffect, useState } from "react";
import { AlertTriangle, Info, Lock } from "lucide-react";
import Card from "./Card";
import { useApi } from "../hooks/useApi";
import {
  asSentence,
  driftDimensionView,
  exposureHorizons,
  feedStatusLabel,
  figureOrReason,
  formatExposure,
  formatFraction,
  formatShare,
  isWithheld,
  listOrReason,
  orderSharedEndpoints,
  ownershipLabel,
  plural,
  sectionState,
  sensitivityLabel,
  tierExposureRows,
  tierLabel,
  topExposureAssets,
  topReachAssets,
} from "../utils/aiFleetPortfolio";

/**
 * The AI Intelligence Portfolio tab: one cross-asset read over aggregates that already exist per
 * asset, from `GET /ai-assets/portfolio` and nothing else — the dashboard's summary read is a
 * separate snapshot, and merging the two would let them disagree on one page.
 *
 * Every unknown renders as the sentence the server gave for it, never as a 0, a dash or an empty
 * bar. A caller who cannot see every deal in the organization gets the withheld sentence and
 * nothing else: no cards, no tables, no placeholders implying figures exist.
 */
export default function AiFleetPortfolioPanel({ orgId, refreshKey }) {
  const apiCall = useApi();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // The only hook-bearing block: every early return below comes after it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await apiCall("GET", "/ai-assets/portfolio");
        if (!cancelled) { setData(result); setError(null); }
      } catch (e) {
        if (!cancelled) { setData(null); setError(e?.message || "could not be loaded"); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, refreshKey]);

  if (loading) return null;

  if (isWithheld(data)) {
    return (
      <Card className="p-4 mb-4">
        <div className="flex items-start gap-2 text-sm text-content-secondary">
          <Lock className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-content-primary">Portfolio withheld</div>
            <p>{asSentence(data.reason)}</p>
          </div>
        </div>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="p-4 mb-4">
        <div className="flex items-center gap-2 text-sm text-content-secondary">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <span className="font-semibold text-content-primary">Portfolio unavailable</span>
          <span className="text-xs">{error ?? "no response"}</span>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <p className="text-xs text-content-secondary mb-3">
        Across {plural(data.activeAssetCount ?? 0, "active AI asset", "active AI assets")}. Exposure
        here is the estimated monetary impact of open findings; running cost is a different number
        and stays on the Usage &amp; Cost tab.
      </p>
      <ExposureSection section={data.exposure} />
      <AccountabilitySection section={data.accountability} />
      <ConcentrationSection section={data.concentration} />
      <ReachSection section={data.reach} />
      <ThirdPartySection section={data.thirdPartyReach} />
      <CoverageSection section={data.coverage} />
    </div>
  );
}

// ── Building blocks ──────────────────────────────────────────────────────────────────────────

function SectionCard({ title, subtitle, children, testId }) {
  return (
    <Card className="p-4 mb-4">
      <section data-testid={testId}>
        <h3 className="text-sm font-semibold text-content-primary">{title}</h3>
        {subtitle && <p className="text-xs text-content-secondary mt-0.5">{subtitle}</p>}
        <div className="mt-3">{children}</div>
      </section>
    </Card>
  );
}

function ReasonLine({ text }) {
  return (
    <p className="flex items-start gap-1.5 text-xs text-content-secondary">
      <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
      <span>{text}</span>
    </p>
  );
}

/** A section that is either its body or its unavailable sentence. */
function Section({ title, subtitle, section, testId, children }) {
  const state = sectionState(section);
  return (
    <SectionCard title={title} subtitle={subtitle} testId={testId}>
      {state.available ? children(state.data) : <ReasonLine text={state.reason} />}
    </SectionCard>
  );
}

function Stat({ label, figure, testId }) {
  return (
    <div className="py-1" data-testid={testId}>
      <div className="text-xs text-content-secondary">{label}</div>
      {figure.kind === "value"
        ? <div className="text-lg font-semibold text-content-primary">{figure.text}</div>
        : <ReasonLine text={figure.text} />}
    </div>
  );
}

function OtherRegion({ reason }) {
  if (!reason) return null;
  return (
    <div className="mt-2" data-testid="other-region-reason">
      <ReasonLine text={asSentence(reason)} />
    </div>
  );
}

function EndpointList({ title, endpoints, emptyText, limit }) {
  const rows = orderSharedEndpoints(endpoints, limit);
  return (
    <div className="mb-3">
      <div className="text-xs font-semibold text-content-secondary uppercase mb-1">{title}</div>
      {rows.length === 0 ? (
        <p className="text-xs text-content-secondary">{emptyText}</p>
      ) : (
        <ul className="space-y-1">
          {rows.map((e) => (
            <li key={e.nodeId} className="flex flex-wrap items-baseline gap-x-2 text-sm">
              <span className="text-content-primary">{e.name}</span>
              <span className="text-xs text-content-secondary">
                reached by {plural(e.callerCount, "asset", "assets")}
              </span>
              {sensitivityLabel(e) && (
                <span className="text-xs text-content-secondary">{sensitivityLabel(e)}</span>
              )}
              {ownershipLabel(e.ownershipDisposition) && (
                <span className="text-xs text-content-secondary">
                  {ownershipLabel(e.ownershipDisposition)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── 1. Exposure concentration ────────────────────────────────────────────────────────────────

function ExposureSection({ section }) {
  return (
    <Section
      title="Exposure concentration"
      subtitle="Estimated monetary impact of open AI posture findings on active assets. Figures are floors: only findings carrying an estimate are priced."
      section={section}
      testId="portfolio-exposure"
    >
      {(x) => {
        const total = figureOrReason(x.totalExposure, x.activeEstateUnavailableReason, formatExposure);
        const top5 = figureOrReason(x.top5Share, x.activeEstateUnavailableReason, formatShare);
        const tiers = tierExposureRows(x.byTier);
        const top = topExposureAssets(x.topAssets);
        const horizons = exposureHorizons(x);
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Stat label="Annual expected loss (active estate)" figure={total} testId="exposure-total" />
              <Stat label="Held by the top 5 assets" figure={top5} testId="exposure-top5" />
              <div className="py-1 text-xs text-content-secondary">
                <div>
                  {plural(x.activeOpenRiskCount, "open finding", "open findings")} on active assets,{" "}
                  {x.unquantifiedRiskCount} without a monetary estimate (counted, not priced).
                </div>
              </div>
            </div>

            {/* Absent, not empty, when the base is null: the base's reason is the only explanation. */}
            {horizons && (
              <div className="mt-3 border-t border-border pt-3" data-testid="exposure-horizons">
                <h4 className="text-xs font-semibold text-content-secondary uppercase">
                  Projection if nothing is remediated
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-1">
                  {horizons.rows.map((h) => (
                    <Stat key={h.key} label={h.label} figure={h.figure} testId={`exposure-horizon-${h.key}`} />
                  ))}
                </div>
                <p className="text-xs text-content-secondary mt-1" data-testid="exposure-projection-assumption">
                  {horizons.assumption}
                </p>
              </div>
            )}

            {/* Declared beside the total, never added into it. */}
            <div className="mt-2 text-xs text-content-secondary space-y-0.5" data-testid="exposure-excluded">
              <div>
                Not in the total: {formatExposure(x.excludedNonActiveExposure)} on{" "}
                {plural(x.excludedNonActiveAssetCount, "asset", "assets")} that{" "}
                {x.excludedNonActiveAssetCount === 1 ? "is" : "are"} no longer active (
                {plural(x.excludedNonActiveRiskCount, "finding", "findings")}).
              </div>
              <div>
                Not in the total: {formatExposure(x.unattributedExposure)} on{" "}
                {plural(x.unattributedRiskCount, "finding", "findings")} not attributed to a known asset.
              </div>
            </div>

            {tiers.length > 0 && (
              <table className="w-full text-sm mt-3" data-testid="exposure-by-tier">
                <tbody>
                  {tiers.map((t) => (
                    <tr key={t.key} data-testid={`tier-row-${t.key}`} className="border-b border-border last:border-0">
                      <td className="py-1 text-content-secondary">{t.label}</td>
                      <td className="py-1 text-right text-content-primary">{formatExposure(t.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {top.length > 0 && (
              <table className="w-full text-sm mt-3">
                <thead>
                  <tr className="text-xs text-content-secondary text-left">
                    <th className="py-1 font-medium">Asset</th>
                    <th className="py-1 font-medium">Tier</th>
                    <th className="py-1 font-medium text-right">Open findings</th>
                    <th className="py-1 font-medium text-right">Exposure</th>
                  </tr>
                </thead>
                <tbody>
                  {top.map((a) => (
                    <tr key={a.assetId} className="border-t border-border">
                      <td className="py-1 text-content-primary">{a.name}</td>
                      <td className="py-1 text-content-secondary">{tierLabel(a.tier)}</td>
                      <td className="py-1 text-right">{a.openRiskCount}</td>
                      <td className="py-1 text-right">{formatExposure(a.exposure)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        );
      }}
    </Section>
  );
}

// ── 2. Accountability ────────────────────────────────────────────────────────────────────────

const ACCOUNTABILITY_ROWS = [
  { key: "ownerPresent", label: "Have an owner" },
  { key: "sponsorPresent", label: "Have a sponsor" },
  { key: "ownerDeclared", label: "Owner declared by a person" },
  { key: "governed", label: "Governed" },
  { key: "shadow", label: "Shadow" },
  { key: "tiered", label: "Tier declared" },
  { key: "untiered", label: "Untiered (no tier declared)" },
  { key: "fullyAccountable", label: "Fully accountable (owner or sponsor, a declared tier, and governed)" },
];

function AccountabilitySection({ section }) {
  return (
    <Section
      title="Accountability"
      subtitle="Of the active estate."
      section={section}
      testId="portfolio-accountability"
    >
      {(a) => {
        const playbook = figureOrReason(a.playbookAssigned, a.playbookUnavailableReason, formatFraction);
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            {ACCOUNTABILITY_ROWS.map(({ key, label }) => (
              <div key={key} className="flex justify-between gap-3 py-1 border-b border-border text-sm">
                <span className="text-content-secondary">{label}</span>
                <span className="text-content-primary">{formatFraction(a[key])}</span>
              </div>
            ))}
            <div className="flex justify-between gap-3 py-1 border-b border-border text-sm">
              <span className="text-content-secondary">Agents with neither owner nor sponsor</span>
              <span className="text-content-primary">{a.ownerlessAgents}</span>
            </div>
            <div className="py-1 border-b border-border text-sm" data-testid="playbook-assigned">
              <div className="flex justify-between gap-3">
                <span className="text-content-secondary">Playbook assigned</span>
                {playbook.kind === "value" && <span className="text-content-primary">{playbook.text}</span>}
              </div>
              {playbook.kind === "reason" && <ReasonLine text={playbook.text} />}
            </div>
          </div>
        );
      }}
    </Section>
  );
}

// ── 3. Concentration ─────────────────────────────────────────────────────────────────────────

function ConcentrationSection({ section }) {
  return (
    <Section
      title="Shared identities, tools and data sources"
      subtitle="Endpoints reached by two or more active assets, top 10 of each."
      section={section}
      testId="portfolio-concentration"
    >
      {(c) => (
        <>
          <EndpointList title="Shared identities" endpoints={c.sharedIdentities}
            emptyText="No identity is reached by two or more active assets in the graph that was read." />
          <EndpointList title="Shared tools" endpoints={c.sharedTools}
            emptyText="No tool is reached by two or more active assets in the graph that was read." />
          <EndpointList title="Shared data sources" endpoints={c.sharedDataSources}
            emptyText="No data source is reached by two or more active assets in the graph that was read." />
          <div className="text-xs font-semibold text-content-secondary uppercase mb-1">Shared credentials</div>
          <ReasonLine text={asSentence(c.sharedCredentialNotAssessedReason)} />
          <OtherRegion reason={c.otherRegionReason} />
        </>
      )}
    </Section>
  );
}

// ── 4. Reach breadth ─────────────────────────────────────────────────────────────────────────

function ReachSection({ section }) {
  return (
    <Section
      title="Reach breadth (first hop only)"
      subtitle="Distinct endpoints each active asset reaches directly: permissions granted, tools, and data accessed. Reach is counted one hop from the asset; whether it chains further cannot be seen."
      section={section}
      testId="portfolio-reach"
    >
      {(r) => {
        const top = topReachAssets(r.topAssets);
        return (
          <>
            {r.reachIsFirstHopOnly && (
              <p className="text-xs text-content-secondary mb-2">First hop only.</p>
            )}
            <div className="text-sm text-content-secondary">
              Widest reach:{" "}
              <span className="text-content-primary font-semibold">
                {plural(r.maxReach, "endpoint", "endpoints")}
              </span>
            </div>
            {top.length > 0 && (
              <table className="w-full text-sm mt-2">
                <thead>
                  <tr className="text-xs text-content-secondary text-left">
                    <th className="py-1 font-medium">Asset</th>
                    <th className="py-1 font-medium">Kind</th>
                    <th className="py-1 font-medium text-right">Endpoints reached (first hop)</th>
                  </tr>
                </thead>
                <tbody>
                  {top.map((a) => (
                    <tr key={a.assetId} className="border-t border-border">
                      <td className="py-1 text-content-primary">{a.name}</td>
                      <td className="py-1 text-content-secondary">{a.kind}</td>
                      <td className="py-1 text-right">{a.reachCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <OtherRegion reason={r.otherRegionReason} />
          </>
        );
      }}
    </Section>
  );
}

// ── 5. Third-party reach ─────────────────────────────────────────────────────────────────────

function ThirdPartySection({ section }) {
  return (
    <Section
      title="Agents reaching a third-party-operated endpoint"
      subtitle="Ownership is set per endpoint by a person in your organization; Axaris does not classify AI assets themselves as third-party. Counted over every active AI asset."
      section={section}
      testId="portfolio-third-party"
    >
      {(t) => (
        <>
          <div className="text-sm text-content-secondary">
            Active assets reaching at least one third-party-operated endpoint:{" "}
            <span className="text-content-primary font-semibold">
              {formatFraction(t.assetsReachingThirdPartyEndpoint)}
            </span>
          </div>
          <div className="text-sm text-content-secondary mt-1" data-testid="unreviewed-endpoints">
            Unreviewed endpoints of any kind (permission, tool or data source):{" "}
            <span className="text-content-primary font-semibold">{t.unreviewedEndpointCount}</span>
            <span className="text-xs"> — nobody has yet recorded who operates them, so they are counted
              neither as yours nor as third-party.</span>
          </div>
          <div className="mt-3">
            <EndpointList title="Third-party-operated endpoints reached" endpoints={t.thirdPartyEndpoints}
              limit={Infinity}
              emptyText="No endpoint reached by an active asset has been marked third-party operated." />
          </div>
          <OtherRegion reason={t.otherRegionReason} />
        </>
      )}
    </Section>
  );
}

// ── 6. What Axaris cannot see ────────────────────────────────────────────────────────────────

function fmtDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
}

/**
 * Inputs switched on in AI settings that have never reported. Each is a sentence naming the input
 * and the server's reason; nothing here is a count, so a missing input never reads as 0.
 */
function UnreportedList({ inputs, testId }) {
  if (!inputs || inputs.length === 0) return null;
  return (
    <ul className="space-y-1 mt-1" data-testid={testId}>
      {inputs.map((u) => (
        <li key={`${u.name}-${u.measurement ?? ""}`} className="text-sm">
          <span className="text-content-primary">{u.name}</span>
          {u.measurement && <span className="text-xs text-content-secondary"> {u.measurement}</span>}
          <span className="block text-xs text-content-secondary">{asSentence(u.reason)}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Rows, the declared unreported inputs, or the empty sentence. An empty row list with unreported
 * inputs declared is not "nothing returned": the declared list says what is missing.
 */
const rowsOrReason = (rows, unreported, emptyReason) => {
  if ((rows ?? []).length === 0 && (unreported ?? []).length > 0) return { rows: [], emptyReason: null };
  return listOrReason(rows, emptyReason);
};

function CoverageSection({ section }) {
  return (
    <Section
      title="What Axaris cannot see"
      subtitle="Behavioural feeds, usage coverage gaps, and behaviour drift that could not be judged."
      section={section}
      testId="portfolio-coverage"
    >
      {(c) => {
        const feeds = rowsOrReason(c.feeds, c.unreportedFeeds, c.feedsEmptyReason);
        const gaps = rowsOrReason(c.usageGaps, c.unreportedUsage, c.usageCoverageEmptyReason);
        const from = fmtDate(c.usageWindowStart);
        const to = fmtDate(c.usageWindowEnd);
        const drift = sectionState(c.drift);
        return (
          <>
            {c.expectedInputsUnknownReason && (
              <div className="mb-3" data-testid="coverage-expected-unknown">
                <ReasonLine text={asSentence(c.expectedInputsUnknownReason)} />
              </div>
            )}
            <div className="text-xs font-semibold text-content-secondary uppercase mb-1">Behavioural feeds</div>
            <div data-testid="coverage-feeds" className="mb-3">
              {feeds.emptyReason ? <ReasonLine text={feeds.emptyReason} /> : feeds.rows.length > 0 && (
                <ul className="space-y-1">
                  {feeds.rows.map((f) => (
                    <li key={f.feed} className="text-sm">
                      <span className="text-content-primary">{f.feed}</span>{" "}
                      <span className="text-xs text-content-secondary">{feedStatusLabel(f.status)}</span>
                      {f.reason && <span className="block text-xs text-content-secondary">{asSentence(f.reason)}</span>}
                    </li>
                  ))}
                </ul>
              )}
              <UnreportedList inputs={c.unreportedFeeds} testId="coverage-feeds-unreported" />
            </div>

            <div className="text-xs font-semibold text-content-secondary uppercase mb-1">
              Usage coverage gaps{from && to ? ` (${from} to ${to})` : ""}
            </div>
            <div data-testid="coverage-usage" className="mb-3">
              {gaps.emptyReason ? <ReasonLine text={gaps.emptyReason} /> : gaps.rows.length > 0 && (
                <ul className="space-y-1">
                  {gaps.rows.map((g) => (
                    <li key={`${g.provider}-${g.measurement}`} className="text-sm">
                      <span className="text-content-primary">{g.provider}</span>{" "}
                      <span className="text-xs text-content-secondary">
                        {g.measurement}: {plural(g.uncoveredDays, "day", "days")} not covered
                      </span>
                      {g.lastFailureReason && (
                        <span className="block text-xs text-content-secondary">{asSentence(g.lastFailureReason)}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <UnreportedList inputs={c.unreportedUsage} testId="coverage-usage-unreported" />
            </div>

            <div className="text-xs font-semibold text-content-secondary uppercase mb-1">Behaviour drift, per dimension</div>
            <p className="text-xs text-content-secondary mb-1" data-testid="drift-explainer">
              Status counts cover the assets in this region that were evaluated. A reason given for an
              evaluated asset that is not Ready describes an asset already counted under its status,
              so those reasons overlap the counts and should not be added to them. Assets stored in
              another region were not evaluated: they appear only as a reason, under no status.
            </p>
            <div data-testid="coverage-drift">
              {!drift.available ? <ReasonLine text={drift.reason} /> : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(drift.data ?? []).map((d) => {
                    const view = driftDimensionView(d);
                    return (
                      <div key={d.dimension} className="text-sm" data-testid={`drift-${d.dimension}`}>
                        <div className="font-medium text-content-primary">{view.label}</div>
                        <ul className="text-xs text-content-secondary">
                          {view.statuses.map((s) => (
                            <li key={s.key}>
                              {s.label}: {s.count === null ? "not reported" : plural(s.count, "asset", "assets")}
                            </li>
                          ))}
                        </ul>
                        {view.withheld.length > 0 && (
                          <ul className="text-xs text-content-secondary mt-1">
                            {view.withheld.map((w) => (
                              <li key={w.reason}>
                                {plural(w.assetCount, "asset", "assets")}: {w.reason}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        );
      }}
    </Section>
  );
}
